import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {
    AuthApi,
    CAApi,
    CertificateApi,
    CertificateStore,
    CipherMailFeature,
    CRLApi,
    DomainApi,
    Expired,
    License,
    LicenseApi,
    MissingKeyAlias,
    MPAMailQueueApi,
    MPAMailRepositoryApi,
    MTAApi,
    PGPKeyringApi,
    PGPKeyType,
    QuarantineApi,
    SMSApi,
    SystemApi,
    SystemService,
    TLSApi,
    UserApi,
    X509CertificateDetails
} from "components/src/api/admin/generated";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useEnterpriseEditionContext} from "components/src/hook/UseEnterpriseEditionContext";
import {useHasAllPermissions} from "components/src/hook/UseHasAllPermissions";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {DateUtils} from "components/src/utils/DateUtils";
import {
    CardShield as SMIMEIcon,
    FingerprintCheckCircle as LicensesIcon,
    Group as OtherIcon,
    Home as DashboardIcon,
    Key as PGPIcon,
    List as QueuesIcon,
    ShieldCheck as TlsIcon,
    ViewGrid as ServicesIcon
} from "iconoir-react";
import {useCallback, useEffect, useState} from "react";
import {Card, CardGroup} from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {Link} from "react-router-dom";
import {DashboardTable} from "./DashboardTable";
import {DashboardTableRow} from "./DashboardTableRow";

/*
 * REST api's for accessing the back-end
 */
const tlsApi = new TLSApi(ApiConfig.config());
const licenseApi = new LicenseApi(ApiConfig.config());
const mailQueueApi = new MPAMailQueueApi(ApiConfig.config());
const mailRepositoryApi = new MPAMailRepositoryApi(ApiConfig.config());
const mtaApi = new MTAApi(ApiConfig.config());
const smsApi = new SMSApi(ApiConfig.config());
const quarantineApi = new QuarantineApi(ApiConfig.config());
const certificateApi = new CertificateApi(ApiConfig.config());
const caApi = new CAApi(ApiConfig.config());
const crlApi = new CRLApi(ApiConfig.config());
const pgpKeyringApi = new PGPKeyringApi(ApiConfig.config());
const systemApi = new SystemApi(ApiConfig.config());
const userApi = new UserApi(ApiConfig.config());
const domainApi = new DomainApi(ApiConfig.config());
const authApi = new AuthApi(ApiConfig.config());

export const Dashboard = () =>
{
    /**
     * The application version
     */
    const [versionString, setVersionString] = useState<string | undefined>("");
    /**
     * The active TLS certificate
     */
    const [activeTLSCertificate, setActiveTLSCertificate] = useState<X509CertificateDetails | undefined>(undefined);
    /**
     * The active license
     */
    const [activeLicense, setActiveLicense] = useState<License | undefined>(undefined);
    /**
     * The number of available user licenses
     */
    const [availableUserLicenses, setAvailableUserLicenses] = useState<number | undefined>(undefined);
    /**
     * The number of items in the MPA outgoing queue
     */
    const [mpaOutgoingQueueSize, setMpaOutgoingQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of items in the MPA spool queue
     */
    const [mpaSpoolQueueSize, setMpaSpoolQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of items in the MPA error queue
     */
    const [mpaErrorQueueSize, setMpaErrorQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of items in the MTA queue
     */
    const [mtaQueueSize, setMtaQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of items in the SMS queue
     */
    const [smsQueueSize, setSmsQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of items in the Quarantine queue
     */
    const [quarantineQueueSize, setQuarantineQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of certificates from the Certificates store
     */
    const [certificateCount, setCertificateCount] = useState<number | undefined>(undefined);
    /**
     * The number of expired certificates from the Certificates store
     */
    const [expiredCertificateCount, setExpiredCertificateCount] = useState<number | undefined>(undefined);
    /**
     * The number of certificates from the Root store
     */
    const [rootCount, setRootCount] = useState<number | undefined>(undefined);
    /**
     * The number of expired certificates from the Roots store
     */
    const [expiredRootCount, setExpiredRootCount] = useState<number | undefined>(undefined);
    /**
     * The number of private keys from the Certificates store
     */
    const [privateKeysCount, setPrivateKeysCount] = useState<number | undefined>(undefined);
    /**
     * The number of pending certificate requests
     */
    const [pendingRequestsCount, setPendingRequestsCount] = useState<number | undefined>(undefined);
    /**
     * The number of CRLs
     */
    const [crlCount, setCrlCount] = useState<number | undefined>(undefined);
    /**
     * The number of PGP keys
     */
    const [pgpKeyCount, setPgpKeyCount] = useState<number | undefined>(undefined);
    /**
     * The number of expired PGP keys
     */
    const [pgpExpiredKeyCount, setPgpExpiredKeyCount] = useState<number | undefined>(undefined);
    /**
     * The number of PGP secret keys
     */
    const [pgpSecretKeyCount, setPgpSecretKeyCount] = useState<number | undefined>(undefined);
    /**
     * The number of Users
     */
    const [userCount, setUserCount] = useState<number | undefined>(undefined);
    /**
     * The number of Domains
     */
    const [domainCount, setDomainCount] = useState<number | undefined>(undefined);
    /**
     * The number of Admins
     */
    const [adminCount, setAdminCount] = useState<number | undefined>(undefined);
    /**
     * True if the portal backend is running
     */
    const [portalBackendRunning, setPortalBackendRunning] = useState<boolean | undefined>(undefined);
    /**
     * True if the company portal backend is running
     */
    const [companyPortalBackendRunning, setCompanyPortalBackendRunning] = useState<boolean | undefined>(undefined);
    /**
     * True if MTA service is running
     */
    const [mtaRunning, setMtaRunning] = useState<boolean | undefined>(undefined);
    /**
     * True if IMAP service is running
     */
    const [imapRunning, setImapRunning] = useState<boolean | undefined>(undefined);
    /**
     * True if there are system updates available
     */
    const [updatesAvailable, setUpdatesAvailable] = useState<boolean | undefined>(undefined);
    /**
     * Incremented whenever the data should be reloaded from the back-end. A counter is used instead of a boolean
     * because every increment is a new value, so a reload is never dropped and the load effects run exactly once
     * per reload request
     */
    const [reloadCounter, setReloadCounter] = useState<number>(0);
    /**
     * Forces the data to be reloaded from the back-end
     */
    const forceReload = useCallback(() => setReloadCounter(counter => counter + 1), []);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    /**
     * A helper for checking whether all required permissions are granted.
     */
    const hasAllPermissions = useHasAllPermissions();
    /**
     * Can be used to check if running on the enterprise edition.
     */
    const enterpriseEditionContext = useEnterpriseEditionContext();

    /**
     * Determines if all required features are available within the active license.
     */
    const hasRequiredFeatures = useCallback((required: ReadonlyArray<CipherMailFeature> = []) =>
    {
        if (!enterpriseEditionContext.enterpriseEdition) {
            // if not running on the enterprise edition, all features are available
            return true;
        }

        if (activeLicense?.features == undefined) {
            return false;
        }

        const features = Array.from(activeLicense.features);

        // the "All" feature is special and always returns true
        if (features.includes(CipherMailFeature.All)) {
            return true;
        }

        return required.every((feature) => feature !== undefined && features.includes(feature));
    }, [activeLicense?.features,
        enterpriseEditionContext.enterpriseEdition]);

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        systemApi.systemControllerVersionString()
            .then(response => {
                if (superseded) {
                    return;
                }

                setVersionString(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setVersionString(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["tls:get-active-certificate"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        tlsApi.tLSControllerGetActiveCertificate()
            .then(response => {
                if (superseded) {
                    return;
                }

                setActiveTLSCertificate(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setActiveTLSCertificate(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["license:get-active-license"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        licenseApi.licenseControllerGetActiveLicense(true)
            .then(response => {
                if (superseded) {
                    return;
                }

                setActiveLicense(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setActiveLicense(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["license:get-available-user-licenses"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        licenseApi.licenseControllerGetAvailableUserLicenses()
            .then(response => {
                if (superseded) {
                    return;
                }

                setAvailableUserLicenses(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setAvailableUserLicenses(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["mail-queue:get-queue-size"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        mailQueueApi.mailQueueControllerGetQueueSize("outgoing")
            .then(response => {
                if (superseded) {
                    return;
                }

                setMpaOutgoingQueueSize(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setMpaOutgoingQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["mail-queue:get-queue-size"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        mailQueueApi.mailQueueControllerGetQueueSize("spool")
            .then(response => {
                if (superseded) {
                    return;
                }

                setMpaSpoolQueueSize(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setMpaSpoolQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["mail-repository:get-size"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        mailRepositoryApi.mailRepositoryControllerGetMailRepositorySize("file://var/mail/error")
            .then(response => {
                if (superseded) {
                    return;
                }

                setMpaErrorQueueSize(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setMpaErrorQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["mta:get-queue-size"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        mtaApi.mTAControllerGetQueueSize()
            .then(response => {
                if (superseded) {
                    return;
                }

                setMtaQueueSize(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setMtaQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["sms:get-sms-queue-size"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        smsApi.sMSControllerGetSMSQueueSize()
            .then(response => {
                if (superseded) {
                    return;
                }

                setSmsQueueSize(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setSmsQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["quarantine:get-quarantined-mail-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        quarantineApi.quarantineControllerGetQuarantinedMailCount()
            .then(response => {
                if (superseded) {
                    return;
                }

                setQuarantineQueueSize(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setQuarantineQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["certificate:get-certificates-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        certificateApi.certificateControllerGetCertificatesCount(CertificateStore.Certificates, Expired.All,
                    MissingKeyAlias.Allowed)
            .then(response => {
                if (superseded) {
                    return;
                }

                setCertificateCount(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setCertificateCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["certificate:get-certificates-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        certificateApi.certificateControllerGetCertificatesCount(CertificateStore.Certificates, Expired.ExpiredOnly,
                    MissingKeyAlias.Allowed)
            .then(response => {
                if (superseded) {
                    return;
                }

                setExpiredCertificateCount(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setExpiredCertificateCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["certificate:get-certificates-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        certificateApi.certificateControllerGetCertificatesCount(CertificateStore.Roots, Expired.All,
            MissingKeyAlias.Allowed)
            .then(response => {
                if (superseded) {
                    return;
                }

                setRootCount(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setRootCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["certificate:get-certificates-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        certificateApi.certificateControllerGetCertificatesCount(CertificateStore.Roots, Expired.ExpiredOnly,
            MissingKeyAlias.Allowed)
            .then(response => {
                if (superseded) {
                    return;
                }

                setExpiredRootCount(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setExpiredRootCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["certificate:get-certificates-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        certificateApi.certificateControllerGetCertificatesCount(CertificateStore.Certificates, Expired.All,
                    MissingKeyAlias.NotAllowed)
            .then(response => {
                if (superseded) {
                    return;
                }

                setPrivateKeysCount(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setPrivateKeysCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["ca:get-pending-requests-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        caApi.cAControllerGetPendingRequestsCount()
            .then(response => {
                if (superseded) {
                    return;
                }

                setPendingRequestsCount(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setPendingRequestsCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["crl:get-crls-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        crlApi.cRLControllerGetCRLsCount()
            .then(response => {
                if (superseded) {
                    return;
                }

                setCrlCount(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setCrlCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["pgp-keyring:get-keys-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        pgpKeyringApi.pGPKeyringControllerGetKeysCount(Expired.All, MissingKeyAlias.Allowed, PGPKeyType.MasterKey)
            .then(response => {
                if (superseded) {
                    return;
                }

                setPgpKeyCount(response.data)
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setPgpKeyCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["pgp-keyring:get-keys-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        pgpKeyringApi.pGPKeyringControllerGetKeysCount(Expired.ExpiredOnly, MissingKeyAlias.Allowed, PGPKeyType.MasterKey)
            .then(response => {
                if (superseded) {
                    return;
                }

                setPgpExpiredKeyCount(response.data)
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setPgpExpiredKeyCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["pgp-keyring:get-keys-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        pgpKeyringApi.pGPKeyringControllerGetKeysCount(Expired.All, MissingKeyAlias.NotAllowed, PGPKeyType.MasterKey)
            .then(response => {
                if (superseded) {
                    return;
                }

                setPgpSecretKeyCount(response.data)
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setPgpSecretKeyCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["system:is-service-running"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        systemApi.systemControllerIsServiceRunning(SystemService.PortalBackend)
            .then(response => {
                if (superseded) {
                    return;
                }

                setPortalBackendRunning(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setPortalBackendRunning(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["system:is-service-running"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        systemApi.systemControllerIsServiceRunning(SystemService.CompanyPortalBackend)
            .then(response => {
                if (superseded) {
                    return;
                }

                setCompanyPortalBackendRunning(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setCompanyPortalBackendRunning(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["system:is-service-running"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        systemApi.systemControllerIsServiceRunning(SystemService.Mta)
            .then(response => {
                if (superseded) {
                    return;
                }

                setMtaRunning(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setMtaRunning(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["system:is-service-running"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        systemApi.systemControllerIsServiceRunning(SystemService.Imap)
            .then(response => {
                if (superseded) {
                    return;
                }

                setImapRunning(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setImapRunning(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["user:get-users-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        userApi.userControllerGetUsersCount()
            .then(response => {
                if (superseded) {
                    return;
                }

                setUserCount(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setUserCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["domain:get-domains-count"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        domainApi.domainControllerGetDomainCount()
            .then(response => {
                if (superseded) {
                    return;
                }

                setDomainCount(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setDomainCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["auth:get-admins"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        authApi.authControllerGetAdmins()
            .then(response => {
                if (superseded) {
                    return;
                }

                setAdminCount(response.data.length);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setAdminCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["system:updates-available"])) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        systemApi.systemControllerUpdatesAvailable()
            .then(response => {
                if (superseded) {
                    return;
                }

                setUpdatesAvailable(response.data);
            })
            .catch(() => {
                if (superseded) {
                    return;
                }

                setUpdatesAvailable(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [reloadCounter,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    /**
     * Determines the CSS class for the available user licenses based on the percentage of licenses in use.
     *
     * This function calculates the percentage of licenses currently in use by comparing the number of available user
     * licenses to the maximum number of users allowed by the active license. Based on the percentage, it returns
     * the appropriate CSS class to visually represent the remaining license availability:
     * - "text-success" if less than 50% of licenses are in use.
     * - "text-warning" if 50% to less than 75% of licenses are in use.
     * - "text-danger" if 75% or more of licenses are in use.
     */
    const availableLicensesCss = () =>
    {
        if (activeLicense == undefined || availableUserLicenses === undefined) {
            return undefined;
        }

        const percentageInUse = (activeLicense.maxUsers - availableUserLicenses) / (activeLicense.maxUsers);

        if (percentageInUse < 0.5) {
            return "text-success"
        }
        else if (percentageInUse < 0.75) {
            return "text-warning"
        }
        else {
            return "text-danger"
        }
    }

    const mtaQueueCss = () =>
    {
        if (mtaQueueSize == undefined) {
            return undefined;
        }

        if (mtaQueueSize < 25) {
            return "text-success"
        }
        else if (mtaQueueSize < 100) {
            return "text-warning"
        }
        else {
            return "text-danger"
        }
    }

    const renderMetrics = () =>
    {
        if (enterpriseEditionContext.enterpriseEdition && activeLicense?.features == undefined) {
            return false;
        }

        return (
            <>
                <Row className="mt-2">
                    <h2 className="mb-4">{t('dashboard.metrics')}</h2>

                    <CardGroup>
                        <Card>
                            <Card.Body className="bg-body-secondary">
                                <div>
                                    <div>{t('dashboard.queues-mta')}</div>
                                    <div className={"fs-1 " + mtaQueueCss()}>{mtaQueueSize}</div>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body className="bg-body-secondary">
                                <div>
                                    <div>{t('dashboard.queues-mpa-error')}</div>
                                    <div className={"fs-1 " + ((mpaErrorQueueSize??0) === 0 ? "text-success" : "text-danger")}>{mpaErrorQueueSize}</div>
                                </div>
                            </Card.Body>
                        </Card>

                        {hasRequiredFeatures([CipherMailFeature.Sms]) &&
                            <Card>
                                <Card.Body className="bg-body-secondary">
                                    <div>
                                        <div>{t('dashboard.queues-sms')}</div>
                                        <div className="text-success fs-1">{smsQueueSize}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        {hasRequiredFeatures([CipherMailFeature.Smime]) &&
                            <Card>
                                <Card.Body className="bg-body-secondary">
                                    <div>
                                        <div>{t('dashboard.smime-pending-requests')}</div>
                                        <div className="text-success fs-1">{pendingRequestsCount}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        {hasRequiredFeatures([CipherMailFeature.Dlp]) &&
                            <Card>
                                <Card.Body className="bg-body-secondary">
                                    <div>
                                        <div>{t('dashboard.queues-quarantine')}</div>
                                        <div className={"fs-1 " + ((quarantineQueueSize??0) === 0 ? "text-success" : "text-danger")}>{quarantineQueueSize}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        {hasRequiredFeatures([CipherMailFeature.Smime]) &&
                            <Card>
                                <Card.Body className="bg-body-secondary">
                                    <div>
                                        <div>{t('dashboard.smime-expired-roots')}</div>
                                        <div className="text-success fs-1">{expiredRootCount}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        {(activeLicense?.maxUsers??0) > 0 &&
                            <Card>
                                <Card.Body className="bg-body-secondary">
                                    <div>
                                        <div>{t('dashboard.available-licenses')}</div>
                                        <div className={"fs-1 " + availableLicensesCss()}>{availableUserLicenses}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }
                    </CardGroup>
                </Row>

                <Row className="mt-4">
                    <CardGroup>
                        <Card className="me-2">
                            <Card.Header><QueuesIcon className="text-success"/> {t('dashboard.queues')}</Card.Header>
                            <Card.Body>
                                <div className="hstack gap-4 align-items-start">
                                    <DashboardTable>
                                        <DashboardTableRow name={t('dashboard.queues-mta')} value={mtaQueueSize} linkProps={{to: "/mta/queue/mta"}}/>
                                        <DashboardTableRow name={t('dashboard.queues-mpa-outgoing')} value={mpaOutgoingQueueSize} linkProps={{to: "/mta/queue/mpa", state: {selectedQueueName: "outgoing"}}}/>
                                        <DashboardTableRow name={t('dashboard.queues-mpa-spool')} value={mpaSpoolQueueSize} linkProps={{to: "/mta/queue/mpa", state: {selectedQueueName: "spool"}}}/>
                                    </DashboardTable>

                                    <DashboardTable>
                                        <DashboardTableRow name={t('dashboard.queues-mpa-error')} value={mpaErrorQueueSize} linkProps={{to: "/mta/queue/repository"}}/>
                                        {hasRequiredFeatures([CipherMailFeature.Sms]) &&
                                            <DashboardTableRow name={t('dashboard.queues-sms')} value={smsQueueSize} linkProps={{to: "/sms"}}/>
                                        }
                                        {hasRequiredFeatures([CipherMailFeature.Dlp]) &&
                                            <DashboardTableRow name={t('dashboard.queues-quarantine')} value={quarantineQueueSize} linkProps={{to: "/dlp/quarantine"}}/>
                                        }
                                    </DashboardTable>
                                </div>
                            </Card.Body>
                        </Card>

                        {hasRequiredFeatures([CipherMailFeature.Smime]) &&
                            <Card className="ms-2 border-start">
                                <Card.Header><SMIMEIcon className="text-success"/> {t('dashboard.smime')}</Card.Header>
                                <Card.Body>
                                    <div className="hstack gap-4 align-items-start">
                                        <DashboardTable>
                                            <DashboardTableRow name={t('dashboard.smime-certificates')} value={certificateCount} linkProps={{to: "/certificates"}}/>
                                            <DashboardTableRow name={t('dashboard.smime-expired-certificates')} value={expiredCertificateCount} linkProps={{to: "/certificates", state: {expired: Expired.ExpiredOnly}}}/>
                                            <DashboardTableRow name={t('dashboard.smime-roots')} value={rootCount} linkProps={{to: "/certificates", state: {certificateStore: CertificateStore.Roots}}}/>
                                            <DashboardTableRow name={t('dashboard.smime-expired-roots')} value={expiredRootCount} linkProps={{to: "/certificates", state: {certificateStore: CertificateStore.Roots, expired: Expired.ExpiredOnly}}}/>
                                        </DashboardTable>

                                        <DashboardTable>
                                            <DashboardTableRow name={t('dashboard.smime-private-keys')} value={privateKeysCount} linkProps={{to: "/certificates", state: {missingKeyAlias: MissingKeyAlias.NotAllowed}}}/>
                                            <DashboardTableRow name={t('dashboard.smime-pending-requests')} value={pendingRequestsCount} linkProps={{to: "/ca"}}/>
                                            <DashboardTableRow name={t('dashboard.smime-crls')} value={crlCount} linkProps={{to: "/crls"}}/>
                                        </DashboardTable>
                                    </div>
                                </Card.Body>
                            </Card>
                        }
                    </CardGroup>
                </Row>

                <Row className="mt-4">
                    <CardGroup>
                        {hasRequiredFeatures([CipherMailFeature.Pgp]) &&
                            <Card className="me-2">
                                <Card.Header><PGPIcon className="text-success"/> {t('dashboard.pgp')}</Card.Header>
                                <Card.Body>
                                    <div className="hstack gap-4 align-items-start">
                                        <DashboardTable>
                                            <DashboardTableRow name={t('dashboard.pgp-keys')} value={pgpKeyCount} linkProps={{to: "/pgp"}}/>
                                            <DashboardTableRow name={t('dashboard.pgp-expired-keys')} value={pgpExpiredKeyCount} linkProps={{to: "/pgp", state: {expired: Expired.ExpiredOnly}}}/>
                                            <DashboardTableRow name={t('dashboard.pgp-secret-keys')} value={pgpSecretKeyCount} linkProps={{to: "/pgp", state: {missingKeyAlias: MissingKeyAlias.NotAllowed}}}/>
                                        </DashboardTable>
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        <Card className="me-2 ms-2 border-start">
                            <Card.Header><OtherIcon className="text-success"/> {t('dashboard.other')}</Card.Header>
                            <Card.Body>
                                <div className="hstack gap-4 align-items-start">
                                    <DashboardTable>
                                        <DashboardTableRow name={t('dashboard.other-users')} value={userCount} linkProps={{to: "/users"}}/>
                                        <DashboardTableRow name={t('dashboard.other-domains')} value={domainCount} linkProps={{to: "/domains"}}/>
                                        <DashboardTableRow name={t('dashboard.other-admins')} value={adminCount}/>
                                    </DashboardTable>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card className="ms-2 border-start">
                            <Card.Header><ServicesIcon className="text-success"/> {t('dashboard.services')}</Card.Header>
                            <Card.Body>
                                <div className="hstack gap-4 align-items-start">
                                    <DashboardTable>
                                        <DashboardTableRow name={t('dashboard.services-portal-backend')} value={portalBackendRunning ? t('dashboard.running') : t('dashboard.not-running')}
                                                           className={portalBackendRunning ? "text-success" : "text-danger"}/>

                                        {enterpriseEditionContext.enterpriseEdition && hasRequiredFeatures([CipherMailFeature.CompanyPortal]) &&
                                            <DashboardTableRow name={t('dashboard.services-company-portal-backend')} value={companyPortalBackendRunning ? t('dashboard.running') : t('dashboard.not-running')}
                                                               className={companyPortalBackendRunning ? "text-success" : "text-danger"}/>
                                        }

                                        <DashboardTableRow name={t('dashboard.services-mta')} value={mtaRunning ? t('dashboard.running') : t('dashboard.not-running')}
                                                           className={mtaRunning ? "text-success" : "text-danger"}/>

                                        {enterpriseEditionContext.enterpriseEdition && hasRequiredFeatures([CipherMailFeature.Webmail]) &&
                                            <DashboardTableRow name={t('dashboard.services-imap')} value={imapRunning ? t('dashboard.running') : t('dashboard.not-running')}
                                                               className={imapRunning ? "text-success" : "text-danger"}/>
                                        }
                                    </DashboardTable>
                                </div>
                            </Card.Body>
                        </Card>
                    </CardGroup>
                </Row>
            </>
        )
    }

    const availableLicenses = () =>
    {
        if (activeLicense?.maxUsers === undefined) {
            return 0;
        }

        return activeLicense?.maxUsers === 0 ? t('dashboard.unlimited-licenses') : <Link to="/licenses" state={{showManageLicensedUsersPopup: true}}>{availableUserLicenses  + "/" + activeLicense?.maxUsers}</Link>;
    }

    return (
        <>
            <PageHeader icon={<DashboardIcon />} title={t("dashboard.title")} onClickReload={forceReload}/>

            <Row className="ms-4 pe-4">
                {
                    updatesAvailable !== undefined && updatesAvailable &&
                    <div>
                        <RenderAlert variant="danger" message={t('dashboard.updates-available')}/>
                    </div>
                }
            </Row>

            <PageContent>
                <Row className="mt-2">
                    <h2 className="mb-4">{t('dashboard.system-info')}</h2>
                    <CardGroup>
                        <Card className="me-2">
                            <Card.Header><TlsIcon className="text-success"/> {t('dashboard.tls-certificate')}</Card.Header>
                            <Card.Body>
                                <Table hover responsive size="sm">
                                    <colgroup>
                                        <col className="col-md-2"/>
                                        <col className="col-md-10"/>
                                    </colgroup>
                                    <tbody>
                                    <tr>
                                        <td>{t('certificate-table-header.subject')}</td>
                                        <td className={"user-select-all"}>{activeTLSCertificate?.subjectFriendly}</td>
                                    </tr>
                                    <tr>
                                        <td>{t('certificate-table-header.issuer')}</td>
                                        <td className={"user-select-all"}>{activeTLSCertificate?.issuerFriendly}</td>
                                    </tr>
                                    <tr>
                                        <td>{t('certificate-table-header.serial-number')}</td>
                                        <td className={"user-select-all"}>{activeTLSCertificate?.serialNumberHex}</td>
                                    </tr>
                                    <tr>
                                        <td>{t('certificate-table-header.not-after')}</td>
                                        <td className={"user-select-all"}>{DateUtils.fromTimestampToLocaleDateString(activeTLSCertificate?.notAfter)}</td>
                                    </tr>
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>

                        <Card className="ms-2 border-start">
                            <Card.Header><LicensesIcon className="text-success"/> {t('dashboard.license')}</Card.Header>
                            <Card.Body>
                                <Table hover responsive size="sm">
                                    <colgroup>
                                        <col className="col-md-2"/>
                                        <col className="col-md-10"/>
                                    </colgroup>
                                    <tbody>
                                    {enterpriseEditionContext.enterpriseEdition &&
                                        <>
                                            <tr>
                                                <td>{t('licenses.company')}</td>
                                                <td className={"user-select-all"}>{activeLicense?.company}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('licenses.expiration-date')}</td>
                                                <td className={"user-select-all"}>{DateUtils.fromTimestampToLocaleDateString(activeLicense?.expirationDate)}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('licenses.comment')}</td>
                                                <td className={"user-select-all"}>{activeLicense?.comments}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('licenses.valid')}</td>
                                                <td className={activeLicense?.valid ? "text-success" : "text-danger"}>{activeLicense?.valid ? t('common.yes') : t('common.no')}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('licenses.features')}</td>
                                                <td className={"user-select-all"}>{Array.from(activeLicense?.features??[]).join(", ")}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('dashboard.available-licenses')}</td>
                                                <td className={"user-select-all"}>{availableLicenses()}</td>
                                            </tr>
                                        </>
                                    }
                                    <tr>
                                        <td>{t('dashboard.version')}</td>
                                        <td className={"user-select-all"}>{versionString}</td>
                                    </tr>
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </CardGroup>
                </Row>

                {renderMetrics()}
            </PageContent>
        </>
    )
}